/**
 * Date utility functions for formatting and manipulating dates
 * across the application.
 */
import { DATE_TIME_TEXTS } from './texts';

/**
 * Formats a date using Intl.DateTimeFormat based on user's locale
 * @param date The date to format (can be null)
 * @param fallbackText Text to display when date is null/undefined
 * @returns Formatted date string in user's locale
 */
export const formatDateTime = (date: Date | null, fallbackText: string = DATE_TIME_TEXTS.noData): string => {
  if (!date) return fallbackText;

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
};

/**
 * Formats a date in a compact format for tables based on user's locale
 * @param date The date to format (can be null)
 * @param fallbackText Text to display when date is null/undefined
 * @returns Compact formatted date string in user's locale
 */
export const formatDateTimeCompact = (date: Date | null, fallbackText: string = DATE_TIME_TEXTS.compactFallback): string => {
  if (!date) return fallbackText;
  
  return new Intl.DateTimeFormat(undefined, {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
};

/**
 * Formats a date as relative time (e.g., "just now", "5 mins ago")
 * @param date The date to format
 * @returns Relative time string
 */
export const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return DATE_TIME_TEXTS.justNow;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return DATE_TIME_TEXTS.minutesAgo(minutes);
  } else {
    return formatTimeLocale(date);
  }
};

/**
 * Formats a date for datetime-local input fields (YYYY-MM-DDTHH:mm)
 * @param date The date to format
 * @returns Formatted datetime string for input field
 */
export const formatDateTimeLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Formats a date for CSV filename generation
 * @param date The date to format (defaults to current date)
 * @returns Object with dateStr (YYYY-MM-DD) and timeStr (HH-MM-SS)
 */
export const formatFilenameDate = (date: Date = new Date()): { dateStr: string; timeStr: string } => {
  const dateStr = date.toISOString().slice(0, 10); // YYYY-MM-DD format
  const timeStr = date.toTimeString().slice(0, 8).replace(/:/g, '-'); // HH-MM-SS format
  return { dateStr, timeStr };
};

/**
 * Calculates the difference in days between two dates
 * @param startDate The start date
 * @param endDate The end date
 * @returns Number of days difference
 */
export const calculateDaysDifference = (startDate: Date, endDate: Date): number => {
  return Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
};

/**
 * Gets the time difference in milliseconds between a date and now
 * @param date The date to compare with current time
 * @returns Time difference in milliseconds
 */
export const getTimeDifference = (date: Date): number => {
  return Date.now() - date.getTime();
};

/**
 * Converts a Date to ISO string format
 * @param date The date to convert
 * @returns ISO string representation
 */
export const toISOString = (date: Date): string => {
  return date.toISOString();
};

/**
 * Gets the user's current timezone from the browser
 * @returns IANA timezone name (e.g., 'America/New_York', 'Europe/Paris', 'UTC')
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Formats a date in a specific timezone
 * @param date The date to format
 * @param timezone The timezone (IANA timezone name, e.g., 'America/New_York', 'Europe/Paris', or 'UTC')
 * @returns Formatted date string in the specified timezone
 */
export const formatDateWithTimezone = (date: Date, timezone: string = 'UTC'): string => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: timezone,
      hour12: false
    }).format(date);
  } catch (error) {
    // Fallback to UTC if timezone is invalid
    console.warn(`Invalid timezone "${timezone}", falling back to UTC`);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC',
      hour12: false
    }).format(date);
  }
};

export const getCurrentYear = (): number => {
  return new Date().getFullYear();
};

/**
 * Formats a date as a locale string with 24-hour format
 * @param date The date to format
 * @param fallbackText Text to display when date is null/undefined
 * @returns Formatted date string in user's locale with 24-hour time
 */
export const formatDateTimeLocale = (date: Date | null, fallbackText: string = DATE_TIME_TEXTS.noData): string => {
  if (!date) return fallbackText;

  return date.toLocaleString(undefined, { hour12: false });
};

/**
 * Formats a time only (no date) in 24-hour format
 * @param date The date to format (only time part will be shown)
 * @param fallbackText Text to display when date is null/undefined
 * @returns Formatted time string in user's locale with 24-hour time
 */
export const formatTimeLocale = (date: Date | null, fallbackText: string = DATE_TIME_TEXTS.noData): string => {
  if (!date) return fallbackText;

  return date.toLocaleTimeString(undefined, { hour12: false });
};